import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AppHeader } from "@/components/AppHeader";
import { useProfile, useIsAdmin } from "@/lib/auth";
import { useAppSettings } from "@/lib/settings";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Wallet as WalletIcon, Plus, ArrowDown, Play, Trophy, ClipboardList, MessageCircle, Send, Shield } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerGamesStatus, pickStatus } from "@/lib/serverGameStatus";
import { SectionErrorBoundary } from "@/components/SectionErrorBoundary";
import { usePageLoadMetric } from "@/lib/diagnostics/usePageLoadMetric";

export const Route = createFileRoute("/_authenticated/")({
  component: HomePage,
});

const INDIAN_NAMES = [
  "Rohan", "Sandeep", "Mohit", "Amit", "Rahul", "Vikas", "Sunil", "Pawan",
  "Deepak", "Ajay", "Ravi", "Karan", "Neeraj", "Manish", "Sonu", "Anil",
  "Kapil", "Jatin",
];

function getRandomInt(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function generateLiveWinners(gamesList: any[]) {
  const count = 5;
  const shuffled = [...INDIAN_NAMES].sort(() => 0.5 - Math.random()).slice(0, count);
  return shuffled.map((name, i) => {
    const gameName = gamesList.length > 0 ? gamesList[getRandomInt(0, gamesList.length - 1)].name : "Mumbai Matka";
    const amount = getRandomInt(10, 500) * 100;
    return { id: i, display_name: name, game_name: gameName, amount };
  });
}

function HomePage() {
  const profileQuery = useProfile();
  const profile = profileQuery.data;
  usePageLoadMetric("home_ready", profileQuery.isLoading);
  const { data: settings } = useAppSettings();
  const { data: isAdmin } = useIsAdmin();
  const navigate = useNavigate();
  const qc = useQueryClient();

  useEffect(() => {
    const channel = supabase
      .channel("games-realtime-home")
      .on("postgres_changes", { event: "*", schema: "public", table: "games" }, () => {
        qc.invalidateQueries({ queryKey: ["games_active"] });
      })
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [qc]);

  const { data: banners } = useQuery({
    queryKey: ["banners"],
    queryFn: async () => {
      const { data } = await supabase.from("banners").select("*").eq("active", true).order("position");
      return data ?? [];
    },
    staleTime: 5 * 60_000,
    placeholderData: [],
  });
  const { data: games } = useQuery({
    queryKey: ["games_active"],
    queryFn: async () => {
      const { data } = await supabase.from("games").select("*").eq("active", true).order("position");
      return data ?? [];
    },
    staleTime: 60_000,
    placeholderData: [],
  });
  const { data: notices } = useQuery({
    queryKey: ["notices_active"],
    queryFn: async () => {
      const { data } = await supabase.from("notices").select("*").eq("active", true).order("created_at", { ascending: false });
      return data ?? [];
    },
    staleTime: 5 * 60_000,
    placeholderData: [],
  });

  // banner slider
  const [slide, setSlide] = useState(0);
  useEffect(() => {
    if (!banners || banners.length < 2) return;
    const t = setInterval(() => setSlide((s) => (s + 1) % banners.length), 3500);
    return () => clearInterval(t);
  }, [banners]);

  const [liveWinners, setLiveWinners] = useState<any[]>([]);
  useEffect(() => {
    if (!games) return;
    setLiveWinners(generateLiveWinners(games));
    const t = setInterval(() => setLiveWinners(generateLiveWinners(games)), 4000);
    return () => clearInterval(t);
  }, [games]);

  return (
    <div>
      <AppHeader />

      {/* Wallet card */}
      <div className="gradient-header px-4 pb-6 -mt-1">
        <div className="rounded-2xl bg-white/15 p-4 backdrop-blur">
          <div className="flex items-center justify-between text-primary-foreground">
            <div>
              <p className="text-xs uppercase tracking-wide text-white/70">Total Wallet Balance</p>
              {profileQuery.isLoading && !profile ? (
                <p className="mt-1 text-3xl font-black opacity-70">₹ —</p>
              ) : profileQuery.isError ? (
                <p className="mt-1 text-sm font-semibold text-white/90">Unable to load wallet balance</p>
              ) : (
                <p className="mt-1 text-3xl font-black">
                  ₹ {(Number(profile?.wallet_balance ?? 0) + Number((profile as any)?.winning_balance ?? 0)).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                </p>
              )}
            </div>
            <WalletIcon className="h-10 w-10 opacity-80" />
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <button onClick={() => navigate({ to: "/wallet" })} className="flex items-center justify-center gap-2 rounded-xl bg-white/90 py-2.5 text-sm font-bold text-primary hover:bg-white">
              <Plus className="h-4 w-4" /> Add Money
            </button>
            <button onClick={() => navigate({ to: "/wallet" })} className="flex items-center justify-center gap-2 rounded-xl bg-white/15 py-2.5 text-sm font-bold text-white hover:bg-white/25">
              <ArrowDown className="h-4 w-4" /> Withdraw
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-4 px-4 py-4">
        {/* Notice ticker */}
        {notices && notices.length > 0 && (
          <div className="overflow-hidden rounded-xl border border-primary/20 bg-accent px-3 py-2">
            <div className="flex gap-8 whitespace-nowrap text-xs font-medium text-primary [animation:marquee_24s_linear_infinite]">
              {notices.map((n) => (<span key={n.id}>📢 {n.message}</span>))}
              {notices.map((n) => (<span key={`${n.id}-2`}>📢 {n.message}</span>))}
            </div>
          </div>
        )}

        {/* Banner slider */}
        {banners && banners.length > 0 && (
          <div className="relative overflow-hidden rounded-2xl shadow-card">
            <div className="relative aspect-[16/8]">
              {banners.map((b, i) => (
                <img
                  key={b.id}
                  src={b.image_url}
                  alt={b.title ?? ""}
                  width={800}
                  height={400}
                  loading={i === 0 ? "eager" : "lazy"}
                  decoding="async"
                  fetchPriority={i === 0 ? "high" : "low"}
                  className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${i === slide ? "opacity-100" : "opacity-0"}`}
                />
              ))}
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              {banners[slide]?.title && (
                <div className="absolute bottom-3 left-4 text-white">
                  <p className="text-sm font-bold drop-shadow">{banners[slide].title}</p>
                </div>
              )}
            </div>
            <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-1">
              {banners.map((_, i) => (
                <span key={i} className={`h-1.5 rounded-full transition-all ${i === slide ? "w-4 bg-white" : "w-1.5 bg-white/50"}`} />
              ))}
            </div>
          </div>
        )}

        {/* Quick actions */}
        <div className="grid grid-cols-3 gap-2">
          {[
            { to: "/matches", label: "Play Now", icon: Play, bg: "bg-success/15", fg: "text-success" },
            { to: "/bets", label: "My Bets", icon: Trophy, bg: "bg-warning/15", fg: "text-warning" },
            { to: "/results", label: "Results", icon: ClipboardList, bg: "bg-primary/15", fg: "text-primary" },
          ].map((a) => {
            const Icon = a.icon;
            return (
              <Link key={a.label} to={a.to} className="flex flex-col items-center gap-1.5 rounded-2xl bg-card p-3 shadow-card">
                <span className={`grid h-11 w-11 place-items-center rounded-xl ${a.bg} ${a.fg}`}>
                  <Icon className="h-5 w-5" />
                </span>
                <span className="text-[11px] font-semibold">{a.label}</span>
              </Link>
            );
          })}
        </div>



        {/* Live winners */}
        <SectionErrorBoundary label="live_winners">
        <section>
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-bold">🔴 Live Winners</h3>
          </div>
          <div className="rounded-2xl bg-card p-3 shadow-card">
            <div className="space-y-2">
              {liveWinners.map((w) => (
                <div key={w.id} className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="grid h-8 w-8 place-items-center rounded-full bg-primary/15 text-xs font-bold text-primary">
                      {w.display_name.slice(0, 2)}
                    </span>
                    <div>
                      <p className="text-sm font-semibold">{w.display_name}</p>
                      <p className="text-[11px] text-muted-foreground">Won in {w.game_name}</p>
                    </div>
                  </div>
                  <span className="text-sm font-black text-success">+₹{Number(w.amount).toLocaleString("en-IN")}</span>
                </div>
              ))}
              {liveWinners.length === 0 && (
                <p className="py-6 text-center text-sm text-muted-foreground">No winners yet — be the first!</p>
              )}
            </div>
          </div>
        </section>
        </SectionErrorBoundary>

        {/* Games */}
        <SectionErrorBoundary label="home_games">
        <section>
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-bold">Today's Games</h3>
            <Link to="/matches" className="text-xs font-semibold text-primary">View All</Link>
          </div>
          <div className="space-y-2">
            {(games ?? []).map((g) => (
              <GameCard key={g.id} game={g} />
            ))}
          </div>
        </section>
        </SectionErrorBoundary>

        {/* Contact buttons */}
        <div className="grid grid-cols-2 gap-2">
          <a href={settings?.contact.whatsapp || "#"} target="_blank" rel="noreferrer"
             className="flex items-center justify-center gap-2 rounded-xl bg-success py-3 text-sm font-bold text-success-foreground shadow-card">
            <MessageCircle className="h-4 w-4" /> WhatsApp
          </a>
          <a href={settings?.contact.telegram || "#"} target="_blank" rel="noreferrer"
             className="flex items-center justify-center gap-2 rounded-xl bg-[#229ED9] py-3 text-sm font-bold text-white shadow-card">
            <Send className="h-4 w-4" /> Telegram
          </a>
        </div>

        {isAdmin && (
          <Link to="/admin" className="flex items-center justify-center gap-2 rounded-xl border border-primary bg-primary/5 py-3 text-sm font-bold text-primary">
            <Shield className="h-4 w-4" /> Open Admin Panel
          </Link>
        )}

        <div className="h-4" />
      </div>

      <style>{`@keyframes marquee { from { transform: translateX(0) } to { transform: translateX(-50%) } }`}</style>
    </div>
  );
}

function GameCard({ game }: { game: any }) {
  const navigate = useNavigate();
  const { data: bulk } = useServerGamesStatus();
  const st = pickStatus(bulk, game.id);
  const isOpen = !!st?.is_betting_open;
  return (
    <div className="rounded-2xl bg-card p-4 shadow-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span
            className="grid h-12 w-12 place-items-center rounded-xl text-lg font-black text-white"
            style={{ background: game.color ?? "var(--color-primary)" }}
          >
            {game.name.slice(0, 1)}
          </span>
          <div>
            <div className="flex items-center gap-2">
              <p className="text-sm font-bold">{game.name}</p>
              <span className={`rounded-full px-2 py-0.5 text-[9px] font-black uppercase ${
                isOpen ? "bg-success/15 text-success" : "bg-destructive/15 text-destructive"
              }`}>
                {isOpen ? "Open" : "Closed"}
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Open {game.open_time?.slice(0, 5)} · Result {game.result_time?.slice(0, 5)}
            </p>
          </div>
        </div>
        <button
          disabled={!isOpen}
          onClick={() => navigate({ to: "/play/$gameId", params: { gameId: game.id } })}
          className="rounded-full bg-primary px-4 py-1.5 text-xs font-bold text-primary-foreground shadow-purple disabled:opacity-50"
        >
          Play
        </button>
      </div>
    </div>
  );
}
